// Push Notification Utility for RenziFitness
// Handles service worker registration and push subscription management

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

/**
 * Convert a URL-safe base64 string to a Uint8Array
 * Required for the applicationServerKey in push subscription
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Detect if running on iOS
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Check if running as installed PWA (standalone mode)
 */
export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://');
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported() {
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasNotification = 'Notification' in window;
  
  console.log('[Push] Support check:', { hasServiceWorker, hasPushManager, hasNotification });
  
  return hasServiceWorker && hasPushManager && hasNotification;
}

/**
 * Check if we're in a secure context (required for push)
 */
export function isSecureContext() {
  return window.isSecureContext || 
         window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1';
}

/**
 * Get the current notification permission status
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Check if user has an active push subscription
 */
export async function isPushEnabled() {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('Error checking push subscription:', error);
    return false;
  }
}

/**
 * Get iOS-specific instructions
 */
export function getIOSInstructions() {
  if (!isIOS()) return null;
  
  if (!isStandalone()) {
    return {
      type: 'not_installed',
      title: 'Add to Home Screen Required',
      message: 'To receive notifications on iPhone:\n\n1. Tap the Share button (square with arrow)\n2. Scroll down and tap "Add to Home Screen"\n3. Open the app from your home screen\n4. Then enable notifications'
    };
  }
  
  return null;
}

/**
 * Register service worker and enable push notifications
 */
export async function enablePushNotifications() {
  console.log('[Push] Starting push notification setup...');
  console.log('[Push] iOS:', isIOS(), 'Standalone:', isStandalone());
  
  // iOS-specific checks
  if (isIOS()) {
    if (!isStandalone()) {
      const instructions = getIOSInstructions();
      throw new Error(instructions.message);
    }
  }
  
  // Check browser support
  if (!isPushSupported()) {
    console.error('[Push] Browser does not support push notifications');
    if (isIOS()) {
      throw new Error('Push notifications require iOS 16.4 or later. Please update your iPhone and try again.');
    }
    throw new Error('Push notifications are not supported in this browser. Try using Chrome, Firefox, or Safari on iOS 16.4+.');
  }
  
  // Check secure context
  if (!isSecureContext()) {
    console.error('[Push] Not in secure context');
    throw new Error('Push notifications require a secure connection (HTTPS)');
  }
  
  console.log('[Push] Requesting notification permission...');
  console.log('[Push] Current permission:', Notification.permission);
  
  // Request notification permission
  let permission;
  try {
    permission = await Notification.requestPermission();
    console.log('[Push] Permission result:', permission);
  } catch (error) {
    console.error('[Push] Permission request failed:', error);
    throw new Error('Failed to request notification permission. Please check your browser settings.');
  }
  
  if (permission !== 'granted') {
    if (permission === 'denied') {
      throw new Error('Notifications are blocked. Please enable them in your browser/device settings:\n\niPhone: Settings → Safari → Notifications → Allow for this website');
    }
    throw new Error('Notification permission was not granted. Please allow notifications when prompted.');
  }
  
  // Register service worker
  let registration;
  try {
    console.log('[Push] Registering service worker...');
    registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
    console.log('[Push] Service Worker registered:', registration);
  } catch (error) {
    console.error('[Push] Service Worker registration failed:', error);
    throw new Error('Failed to register service worker: ' + error.message);
  }
  
  // Wait for service worker to be ready
  console.log('[Push] Waiting for service worker to be ready...');
  await navigator.serviceWorker.ready;
  console.log('[Push] Service worker ready');
  
  // Always start fresh: an old subscription may be tied to a previous key and get rejected by Apple
  let subscription = await registration.pushManager.getSubscription();
  const oldEndpoint = subscription ? subscription.endpoint : null;
  if (subscription) {
    console.log('[Push] Removing existing subscription to create a fresh one');
    await subscription.unsubscribe().catch(() => {});
    subscription = null;
  }
  
  if (!subscription) {
    // Always use the server's key so it matches the key notifications are signed with
    let publicKey = '';
    {
      try {
        console.log('[Push] Fetching VAPID key from backend...');
        const response = await fetch(`${API_URL}/api/push/public-key`, {
          credentials: 'include',
          cache: 'no-store'
        });
        if (response.ok) {
          const data = await response.json();
          publicKey = data.publicKey;
          console.log('[Push] VAPID key fetched from backend');
        }
      } catch (error) {
        console.error('[Push] Failed to fetch VAPID public key:', error);
      }
    }
    
    if (!publicKey) publicKey = VAPID_PUBLIC_KEY;
    if (!publicKey) {
      throw new Error('VAPID public key not available. Please contact support.');
    }
    
    // Subscribe to push
    console.log('[Push] Creating push subscription...');
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      console.log('[Push] Push subscription created successfully');
    } catch (error) {
      console.error('[Push] Failed to create subscription:', error);
      if (error.name === 'NotAllowedError') {
        throw new Error('Push subscription was blocked. Please check your notification settings.');
      }
      throw new Error('Failed to create push subscription: ' + error.message);
    }
  }
  
  // Send subscription to backend
  console.log('[Push] Saving subscription to server...');
  console.log('[Push] API URL:', API_URL);
  
  try {
    // Get session token from cookie or localStorage
    const getSessionToken = () => {
      // Try cookie first
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'session_token') return value;
      }
      // Try localStorage
      return localStorage.getItem('session_token');
    };
    
    const sessionToken = getSessionToken();
    console.log('[Push] Has session token:', !!sessionToken);
    
    const headers = { 'Content-Type': 'application/json' };
    
    // If we have a session token, add it as a cookie header won't work cross-origin
    // Instead pass it in a custom header that the backend can read
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }
    
    const response = await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: headers,
      credentials: 'include',
      body: JSON.stringify(subscription.toJSON())
    });
    
    console.log('[Push] Server response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Push] Failed to save subscription:', response.status, errorText);
      
      if (response.status === 401) {
        throw new Error('Session expired. Please log out and log back in, then try enabling notifications again.');
      }
      throw new Error(`Failed to save push subscription (${response.status}). Please try again.`);
    }
    
    const result = await response.json();
    if (oldEndpoint && oldEndpoint !== subscription.endpoint) {
      fetch(`${API_URL}/api/push/subscribe?endpoint=${encodeURIComponent(oldEndpoint)}`, {
        method: 'DELETE', credentials: 'include', headers
      }).catch(() => {});
    }
    console.log('[Push] ✅ Push notifications enabled successfully! User ID:', result.user_id);
    return subscription;
  } catch (fetchError) {
    console.error('[Push] Fetch error:', fetchError);
    if (fetchError.message.includes('log in') || fetchError.message.includes('Session')) {
      throw fetchError;
    }
    throw new Error('Network error while saving subscription. Check your connection and try again.');
  }
}

/**
 * Disable push notifications
 */
export async function disablePushNotifications() {
  if (!isPushSupported()) return;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Unsubscribe from push
      await subscription.unsubscribe();
      
      // Remove subscription from backend
      const token = localStorage.getItem('session_token');
      await fetch(`${API_URL}/api/push/subscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { 'X-Session-Token': token } : {}
      });
      
      console.log('Push notification subscription removed');
    }
  } catch (error) {
    console.error('Error disabling push notifications:', error);
    throw error;
  }
}

/**
 * Send a test push notification
 */
export async function sendTestPush() {
  // Get session token from localStorage
  const sessionToken = localStorage.getItem('session_token');
  
  const headers = {};
  if (sessionToken) {
    headers['X-Session-Token'] = sessionToken;
  }
  
  const response = await fetch(`${API_URL}/api/push/test`, {
    method: 'POST',
    credentials: 'include',
    headers: headers
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to send test notification');
  }
  
  return response.json();
}

export default {
  isPushSupported,
  isSecureContext,
  getNotificationPermission,
  isPushEnabled,
  enablePushNotifications,
  disablePushNotifications,
  sendTestPush,
  isIOS,
  isStandalone,
  getIOSInstructions
};
